// =============================================================================
// COMPONENTS/NOVA_OPERACAO_MODAL.TSX - Sessão 7B
// =============================================================================
// Modal para criação de nova operação com geração automática de pasta SharePoint.
// Campos obrigatórios: origem, nomeComprador, documentoComprador.
// Campos opcionais: valorImovel.
// Sessão 7B: alerta não-bloqueante quando CPF/CNPJ já existe no cadastro.
//   Um CPF pode ser comprador numa operação e vendedor em outra - não bloquear.
// =============================================================================

import { useState, useEffect, useRef, useCallback, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, FolderOpen } from 'lucide-react';
import { operacoesApi, partesApi, isolveApi, ocupacoesApi, type ErroISolve } from '../services/api';
import type { Operacao, ItemSemCcv, Ocupacao } from '../types';
import { toastErroISolve } from '../utils/toastErroISolve';

// SPEC_LAYOUT_UX_V1 (E4, 06/07/2026) - confirmado em campo (Géssica): estes 4
// campos estão sempre disponíveis na entrada da operação.
const ESTADOS_CIVIS: { value: string; label: string }[] = [
  { value: 'SOLTEIRO', label: 'Solteiro(a)' },
  { value: 'CASADO', label: 'Casado(a)' },
  { value: 'UNIAO_ESTAVEL', label: 'União estável' },
  { value: 'DIVORCIADO', label: 'Divorciado(a)' },
  { value: 'DIVORCIADO_SEM_PARTILHA', label: 'Divorciado(a) sem partilha' },
  { value: 'SEPARADO_FATO', label: 'Separado(a) de fato' },
  { value: 'VIUVO', label: 'Viúvo(a)' },
  { value: 'VIUVO_INVENTARIO_ABERTO', label: 'Viúvo(a) - inventário aberto' },
];

type OrigemOperacao = 'QUINTO_ANDAR' | 'S_QUATRO' | 'VAREJO';

interface NovaOperacaoModalProps {
  onClose: () => void;
  onCriada: (op: Operacao) => void;
  // Caminho da exceção pré-CCV (SPEC 3, E3) - chamado após importação bem-sucedida
  onImportadoSemCcv?: (operacaoId: string) => void;
}

function formatarValorExibicao(v: number | null): string {
  if (v == null) return '-';
  return v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 });
}

function formatarDataExibicao(iso: string | null): string {
  if (!iso) return '-';
  return new Date(iso).toLocaleDateString('pt-BR');
}

const ORIGENS: { value: OrigemOperacao; label: string; prefixo: string }[] = [
  { value: 'QUINTO_ANDAR', label: 'Quinto Andar',  prefixo: '5A'  },
  { value: 'S_QUATRO',     label: 'S.Quatro',      prefixo: 'S4'  },
  { value: 'VAREJO',       label: 'Varejo',         prefixo: 'VAR' },
];

function formatarCPF(valor: string): string {
  const digits = valor.replace(/\D/g, '').slice(0, 11);
  return digits
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})\.(\d{3})(\d)/, '$1.$2.$3')
    .replace(/\.(\d{3})(\d)/, '.$1-$2');
}

function formatarCNPJ(valor: string): string {
  const digits = valor.replace(/\D/g, '').slice(0, 14);
  return digits
    .replace(/(\d{2})(\d)/, '$1.$2')
    .replace(/(\d{3})\.(\d{3})(\d)/, '$1.$2.$3')
    .replace(/\.(\d{3})(\d)/, '.$1/$2')
    .replace(/\/(\d{4})(\d)/, '/$1-$2');
}

// Máscara de moeda BRL - digita centavos, exibe "1.234,56"
function formatarMoeda(valor: string): string {
  const digits = valor.replace(/\D/g, '').slice(0, 12); // até R$ 9.999.999.999,99
  if (!digits) return '';
  const numero = Number(digits) / 100;
  return numero.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// Converte o valor mascarado de volta para número (ex: "1.234,56" → 1234.56)
function moedaParaNumero(mascarado: string): number | undefined {
  const digits = mascarado.replace(/\D/g, '');
  if (!digits) return undefined;
  return Number(digits) / 100;
}

export default function NovaOperacaoModal({ onClose, onCriada, onImportadoSemCcv }: NovaOperacaoModalProps) {
  const navigate = useNavigate();
  const [modo, setModo] = useState<'manual' | 'buscar'>('manual');

  const [origem, setOrigem]                         = useState<OrigemOperacao>('QUINTO_ANDAR');
  const [nomeComprador, setNomeComprador]           = useState('');
  const [documentoComprador, setDocumentoComprador] = useState('');
  const [tipoPessoa, setTipoPessoa]                 = useState<'PF' | 'PJ'>('PF');
  const [valorImovel, setValorImovel]               = useState('');
  // OTMC-* (07/09/2026) - vínculo manual ao idCstimob (solicitacaoId) do iSolve;
  // o Sincronizar completa CCV/proposta ao encontrar a operação por esse idCstimob.
  const [idCstimob, setIdCstimob]                   = useState('');
  const [salvando, setSalvando]                     = useState(false);
  const [erro, setErro]                             = useState<string | null>(null);

  // SPEC_LAYOUT_UX_V1 (E4) - 4 campos obrigatórios do intake
  const [estadoCivil, setEstadoCivil]               = useState('');
  const [ocupacaoId, setOcupacaoId]                 = useState('');
  const [condicaoImovel, setCondicaoImovel]         = useState<'' | 'NOVO' | 'USADO'>('');
  const [percentualRenda, setPercentualRenda]       = useState('100');
  const [ocupacoes, setOcupacoes]                   = useState<Ocupacao[]>([]);

  useEffect(() => {
    ocupacoesApi.listar().then(setOcupacoes).catch(() => { /* seletor fica vazio - achado conhecido (dicionário 0% populado) */ });
  }, []);
  // Sessão 7B: alerta não-bloqueante de CPF/CNPJ já cadastrado
  const [alertaDocumento, setAlertaDocumento]       = useState<string | null>(null);
  // Debounce para não disparar a busca a cada tecla
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ── "Buscar no iSolve" - caminho da exceção pré-CCV (SPEC 3, E3) ──────────
  const [carregandoBusca, setCarregandoBusca] = useState(false);
  const [itensBusca, setItensBusca]           = useState<ItemSemCcv[]>([]);
  const [truncadoBusca, setTruncadoBusca]     = useState(false);
  const [erroBusca, setErroBusca]             = useState<string | null>(null);
  const [termoFiltro, setTermoFiltro]         = useState('');
  const [importandoId, setImportandoId]       = useState<string | null>(null);
  const [buscaCarregada, setBuscaCarregada]   = useState(false);

  // Enriquecimento em 2º plano do valor de financiamento desejado - REFINO 02/07/2026
  // item 2. score/consultas não traz esse campo (confirmado em live); só existe em
  // score/{idCstimob}/detalhes, buscado linha a linha depois que a lista já renderizou.
  // Cache por idCstimob na sessão do modal - Recarregar não rebusca o que já tem.
  const valorCacheRef = useRef<Map<string, number | null>>(new Map());
  // Token de carga - incrementado a cada carregarSemCcv para invalidar workers de
  // enriquecimento de uma carga anterior (evita corrida entre Recarregar e o scan em andamento).
  const enriquecimentoTokenRef = useRef(0);
  // BUG 02/07/2026 (feedback live): em React.StrictMode (dev), o efeito de montagem roda
  // mount→cleanup→remount. Setar `false` só no cleanup e NUNCA voltar a `true` no corpo do
  // efeito deixava a ref travada em `false` após o 1º ciclo - todo worker de enriquecimento
  // retornava ANTES do primeiro `await`, sem nenhuma chamada de rede. Placeholder "…" ficava
  // preso para sempre em toda linha. Corrigido: o corpo do efeito também reseta para `true`.
  const montadoRef = useRef(true);
  useEffect(() => {
    montadoRef.current = true;
    return () => { montadoRef.current = false; };
  }, []);
  // Ids ainda sem valor resolvido - só para exibir placeholder na linha (item.valor
  // sozinho não distingue "ainda buscando" de "confirmado sem valor").
  const [valoresPendentes, setValoresPendentes] = useState<Set<string>>(new Set());

  const enriquecerValores = useCallback((itens: ItemSemCcv[], token: number) => {
    const pendentes = itens.filter((i) => !valorCacheRef.current.has(i.idCstimob));
    setValoresPendentes(new Set(pendentes.map((i) => i.idCstimob)));
    if (pendentes.length === 0) return;

    const CONCORRENCIA = 2; // limitada - respeita o rate limiting já embutido no cliente iSolve
    let cursor = 0;

    const worker = async () => {
      while (cursor < pendentes.length) {
        if (enriquecimentoTokenRef.current !== token || !montadoRef.current) return;
        const item = pendentes[cursor++];
        let valor: number | null = null;
        try {
          valor = await isolveApi.buscarValorFinanciamento(item.idCstimob);
        } catch (e: unknown) {
          // Best-effort por item - não interrompe a listagem nem gera toast por linha,
          // mas o erro precisa ficar visível no console (silêncio total escondeu o bug
          // do StrictMode até virar report de produção - nunca mais engolir sem log).
          console.warn(`[buscarValorFinanciamento] falhou para idCstimob=${item.idCstimob}:`, e);
        }
        if (enriquecimentoTokenRef.current !== token || !montadoRef.current) return;
        valorCacheRef.current.set(item.idCstimob, valor);
        setItensBusca((prev) => prev.map((p) => (p.idCstimob === item.idCstimob ? { ...p, valor } : p)));
        setValoresPendentes((prev) => {
          if (!prev.has(item.idCstimob)) return prev;
          const next = new Set(prev);
          next.delete(item.idCstimob);
          return next;
        });
      }
    };

    for (let i = 0; i < CONCORRENCIA; i++) void worker();
  }, []);

  const carregarSemCcv = useCallback(async () => {
    setCarregandoBusca(true);
    setErroBusca(null);
    const token = ++enriquecimentoTokenRef.current; // invalida qualquer enriquecimento anterior em andamento
    try {
      const resultado = await isolveApi.listarSemCcv();
      // Aplica o cache já conhecido (Recarregar não rebusca o que já tem)
      const itensComCache = resultado.itens.map((item) => {
        const cacheado = valorCacheRef.current.get(item.idCstimob);
        return cacheado !== undefined ? { ...item, valor: cacheado } : item;
      });
      setItensBusca(itensComCache);
      setTruncadoBusca(resultado.truncado);
      setBuscaCarregada(true);
      enriquecerValores(itensComCache, token); // dispara em 2º plano, não aguarda
    } catch (e: unknown) {
      const erroDiscriminador = (e as { response?: { data?: { erro?: string } } })?.response?.data?.erro as ErroISolve | undefined;
      if (erroDiscriminador) {
        toastErroISolve(erroDiscriminador, navigate);
      } else {
        setErroBusca('Erro ao buscar propostas no iSolve - tente novamente.');
      }
    } finally {
      setCarregandoBusca(false);
    }
  }, [navigate, enriquecerValores]);

  // Carrega automaticamente na 1ª vez que o operador entra no modo "Buscar"
  useEffect(() => {
    if (modo === 'buscar' && !buscaCarregada && !carregandoBusca) {
      carregarSemCcv();
    }
  }, [modo, buscaCarregada, carregandoBusca, carregarSemCcv]);

  const itensFiltrados = itensBusca.filter((item) => {
    const t = termoFiltro.trim().toLowerCase();
    if (!t) return true;
    return (item.nome ?? '').toLowerCase().includes(t) || item.idCstimob.includes(t);
  });

  async function handleImportarSemCcv(idCstimob: string) {
    setImportandoId(idCstimob);
    setErroBusca(null);
    try {
      const resultado = await isolveApi.importarOperacaoPreProposta(idCstimob);
      if (resultado.ok) {
        onImportadoSemCcv?.(resultado.operacaoId);
      } else {
        setErroBusca(resultado.motivo);
      }
    } catch (e: unknown) {
      const erroDiscriminador = (e as { response?: { data?: { erro?: string } } })?.response?.data?.erro as ErroISolve | undefined;
      if (erroDiscriminador) {
        toastErroISolve(erroDiscriminador, navigate);
      } else {
        setErroBusca('Erro ao importar - tente novamente.');
      }
    } finally {
      setImportandoId(null);
    }
  }

  // Fecha com ESC
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  const handleDocumento = (val: string) => {
    const formatted = tipoPessoa === 'PF' ? formatarCPF(val) : formatarCNPJ(val);
    setDocumentoComprador(formatted);
    setAlertaDocumento(null);

    const digits = formatted.replace(/\D/g, '');
    const tamanhoEsperado = tipoPessoa === 'PF' ? 11 : 14;

    // Só consulta quando o documento estiver completo
    if (digits.length === tamanhoEsperado) {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(async () => {
        try {
          const resultado = await partesApi.buscar(digits);
          if (resultado.encontrado) {
            // Alerta informativo - NÃO bloqueia o submit
            setAlertaDocumento(
              `${tipoPessoa === 'PF' ? 'CPF' : 'CNPJ'} já cadastrado como "${resultado.nome}". ` +
              `O cadastro existente será mantido (o nome digitado aqui será ignorado).`
            );
          }
        } catch {
          // Falha silenciosa - alerta não é crítico
        }
      }, 400);
    }
  };

  // Limpa debounce ao desmontar
  useEffect(() => () => { if (debounceRef.current) clearTimeout(debounceRef.current); }, []);

  const documentoSemMascara = documentoComprador.replace(/\D/g, '');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setErro(null);

    // Validação mínima
    if (!nomeComprador.trim()) {
      setErro('Informe o nome do comprador.');
      return;
    }
    const docLen = documentoSemMascara.length;
    if (tipoPessoa === 'PF' && docLen !== 11) {
      setErro('CPF deve ter 11 dígitos.');
      return;
    }
    if (tipoPessoa === 'PJ' && docLen !== 14) {
      setErro('CNPJ deve ter 14 dígitos.');
      return;
    }
    // SPEC_LAYOUT_UX_V1 (E4) - 4 campos obrigatórios (confirmado em campo,
    // Géssica, 06/07). Ocupação só é bloqueante se o dicionário tiver opções
    // (achado: 0% populado em produção hoje - ver relatório da etapa).
    if (!estadoCivil) { setErro('Informe o estado civil do comprador.'); return; }
    if (!condicaoImovel) { setErro('Informe a condição do imóvel (novo ou usado).'); return; }
    if (percentualRenda.trim() === '' || Number(percentualRenda) < 0 || Number(percentualRenda) > 100) {
      setErro('Informe a composição de renda (0 a 100%).');
      return;
    }
    if (ocupacoes.length > 0 && !ocupacaoId) { setErro('Informe a ocupação do comprador.'); return; }

    setSalvando(true);
    try {
      const novaOp = await operacoesApi.criar({
        origem,
        nomeComprador:      nomeComprador.trim(),
        documentoComprador: documentoSemMascara,
        valorImovel:        moedaParaNumero(valorImovel),
        idCstimob:          idCstimob.trim() ? Number(idCstimob.trim()) : undefined,
        estadoCivil,
        ocupacaoId:         ocupacaoId || undefined,
        condicaoImovel:     condicaoImovel || undefined,
        percentualComposicaoRenda: Number(percentualRenda),
      });
      onCriada(novaOp);
    } catch (err: any) {
      const msg = err?.response?.data?.erro ?? err?.message ?? 'Erro ao criar operação.';
      setErro(msg);
    } finally {
      setSalvando(false);
    }
  };

  const origemSelecionada = ORIGENS.find((o) => o.value === origem)!;
  const previewPasta = nomeComprador.trim() && documentoComprador
    ? `${origemSelecionada.prefixo} - ${nomeComprador.trim()} (${documentoSemMascara})`
    : null;

  return (
    <div className="fixed inset-0 z-50 flex">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />

      {/* Drawer lateral (direita) */}
      <div className={`relative ml-auto h-full w-full bg-white shadow-2xl flex flex-col overflow-y-auto ${modo === 'buscar' ? 'sm:max-w-lg' : 'sm:max-w-md'}`}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 sticky top-0 bg-white z-10">
          <div>
            <h2 className="text-base font-semibold text-slate-800">Nova Operação</h2>
            <p className="text-xs text-slate-500 mt-0.5">
              {modo === 'manual' ? 'Pasta SharePoint criada automaticamente' : 'Exceção: importar antes da assinatura do CCV'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Toggle de modo */}
        <div className="flex gap-2 px-5 pt-4">
          <button
            type="button"
            onClick={() => setModo('manual')}
            className={`flex-1 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
              modo === 'manual'
                ? 'bg-slate-800 text-white border-slate-800'
                : 'bg-white text-slate-500 border-slate-300 hover:border-slate-400'
            }`}
          >
            Cadastro manual
          </button>
          <button
            type="button"
            onClick={() => setModo('buscar')}
            className={`flex-1 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
              modo === 'buscar'
                ? 'bg-slate-800 text-white border-slate-800'
                : 'bg-white text-slate-500 border-slate-300 hover:border-slate-400'
            }`}
          >
            Buscar no iSolve
          </button>
        </div>

        {modo === 'buscar' ? (
          <div className="p-5 space-y-3">
            <p className="text-xs text-slate-500">
              Operações SEM CCV assinado, últimos 30 dias por data de entrada, ainda não importadas -
              inclui operações "em preenchimento" (antes de qualquer proposta). Uma a uma, por escolha explícita.
            </p>

            <div className="flex gap-2">
              <input
                type="text"
                value={termoFiltro}
                onChange={(e) => setTermoFiltro(e.target.value)}
                placeholder="Filtrar por nome ou idCstimob..."
                className="flex-1 px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <button
                type="button"
                onClick={carregarSemCcv}
                disabled={carregandoBusca}
                className="px-3 py-2 border border-slate-300 rounded-lg text-xs font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-50 transition-colors"
              >
                {carregandoBusca ? '…' : 'Recarregar'}
              </button>
            </div>

            {/* Contador de completude - item 4 do REFINO 02/07 */}
            {buscaCarregada && !carregandoBusca && (
              <p className="text-xs text-slate-500">
                {itensBusca.length} operaç{itensBusca.length !== 1 ? 'ões' : 'ão'} · janela 30 dias
                {termoFiltro.trim() && ` · ${itensFiltrados.length} filtrada${itensFiltrados.length !== 1 ? 's' : ''}`}
              </p>
            )}

            {truncadoBusca && (
              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                Mostrando os primeiros 100 resultados - pode haver mais. Se persistir, investigar (não é esperado numa janela de 30 dias).
              </p>
            )}

            {erroBusca && (
              <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2 rounded-lg">
                {erroBusca}
              </div>
            )}

            <div className="max-h-80 overflow-y-auto border border-slate-200 rounded-lg divide-y divide-slate-100">
              {carregandoBusca && itensBusca.length === 0 && (
                <div className="py-6 text-center">
                  <p className="text-sm text-slate-500">Carregando…</p>
                  <p className="text-xs text-slate-400 mt-1">A busca pode levar até ~1 min.</p>
                </div>
              )}
              {!carregandoBusca && buscaCarregada && itensFiltrados.length === 0 && (
                <p className="text-sm text-slate-500 py-6 text-center">Nenhuma operação sem CCV encontrada.</p>
              )}
              {itensFiltrados.map((item) => {
                // Visibilidade total, importação condicionada (F4/P5a): sem CPF, a linha
                // aparece mas o botão fica desabilitado - o Emerson precisa enxergar o
                // funil inteiro, mas nunca cria operação sem âncora de identidade.
                const semCpf = item.cpf === '-';
                return (
                  <div key={item.idCstimob} className="flex items-center justify-between gap-3 px-3 py-2.5">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-slate-800 truncate">
                        {item.nome ?? 'Sem nome'}
                        {item.offer && (
                          <span className="text-xs font-normal text-slate-400 ml-1.5">offer {item.offer}</span>
                        )}
                      </p>
                      <p className="text-xs text-slate-500">
                        {item.cpf ?? '-'} · {item.situacao}{item.subestado ? ` · ${item.subestado}` : ''}
                        {valoresPendentes.has(item.idCstimob)
                          ? ' · valor: …'
                          : item.valor != null && ` · ${formatarValorExibicao(item.valor)}`} · {formatarDataExibicao(item.dataEntrada)}
                      </p>
                      {item.parceiro && (
                        <p className="text-[11px] text-slate-400 truncate">Parceiro: {item.parceiro}</p>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => handleImportarSemCcv(item.idCstimob)}
                      disabled={importandoId !== null || semCpf}
                      title={semCpf ? 'sem CPF no iSolve ainda' : undefined}
                      className="shrink-0 text-xs font-medium text-white bg-otm-orange px-3 py-1.5 rounded-lg hover:bg-otm-orange-dark disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {importandoId === item.idCstimob ? 'Importando…' : 'Importar'}
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
        /* Formulário */
        <form onSubmit={handleSubmit} className="p-5 space-y-4">

          {/* Origem */}
          <div>
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide block mb-1.5">
              Origem *
            </label>
            <div className="flex gap-2">
              {ORIGENS.map((o) => (
                <button
                  key={o.value}
                  type="button"
                  onClick={() => setOrigem(o.value)}
                  className={`flex-1 py-2 rounded-lg text-sm font-medium border transition-colors ${
                    origem === o.value
                      ? 'bg-otm-orange text-white border-otm-orange'
                      : 'bg-white text-slate-600 border-slate-300 hover:border-blue-400'
                  }`}
                >
                  <span className="font-mono text-xs block">{o.prefixo}</span>
                  <span className="text-[11px] block mt-0.5 opacity-80">{o.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Nome do comprador */}
          <div>
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide block mb-1.5">
              Nome completo do comprador *
            </label>
            <input
              type="text"
              value={nomeComprador}
              onChange={(e) => setNomeComprador(e.target.value)}
              placeholder="Ex: João da Silva Souza"
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              autoFocus
            />
          </div>

          {/* Tipo de pessoa + documento */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                Documento do comprador *
              </label>
              <div className="flex gap-1">
                {(['PF', 'PJ'] as const).map((t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => { setTipoPessoa(t); setDocumentoComprador(''); setAlertaDocumento(null); }}
                    className={`text-xs px-2 py-0.5 rounded font-medium transition-colors ${
                      tipoPessoa === t
                        ? 'bg-slate-800 text-white'
                        : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                    }`}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>
            <input
              type="text"
              value={documentoComprador}
              onChange={(e) => handleDocumento(e.target.value)}
              placeholder={tipoPessoa === 'PF' ? '000.000.000-00' : '00.000.000/0000-00'}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            {/* Alerta não-bloqueante - CPF/CNPJ já existe no cadastro */}
            {alertaDocumento && (
              <div className="mt-1.5 bg-amber-50 border border-amber-200 text-amber-800 text-xs px-3 py-2 rounded-lg flex gap-2">
                <AlertTriangle size={14} className="shrink-0 mt-0.5" />
                <span>{alertaDocumento}</span>
              </div>
            )}
          </div>

          {/* Valor do imóvel */}
          <div>
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide block mb-1.5">
              Valor do imóvel <span className="font-normal normal-case">(opcional)</span>
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-slate-400">R$</span>
              <input
                type="text"
                inputMode="numeric"
                value={valorImovel}
                onChange={(e) => setValorImovel(formatarMoeda(e.target.value))}
                placeholder="0,00"
                className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* OTMC-* (07/09/2026) - idCstimob (solicitacaoId) do iSolve p/ o Sincronizar associar */}
          <div>
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide block mb-1.5">
              ID iSolve (idCstimob) <span className="font-normal normal-case">(opcional)</span>
            </label>
            <input
              type="text"
              inputMode="numeric"
              value={idCstimob}
              onChange={(e) => setIdCstimob(e.target.value.replace(/\D/g, ''))}
              placeholder="Ex: 685306"
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <p className="text-[11px] text-slate-400 mt-1">
              Se preenchido, o "Sincronizar" associa o CCV/proposta do iSolve a esta operação.
            </p>
          </div>

          {/* SPEC_LAYOUT_UX_V1 (E4) - 4 campos obrigatórios do intake */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide block mb-1.5">
                Estado civil *
              </label>
              <select
                value={estadoCivil}
                onChange={(e) => setEstadoCivil(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Selecione…</option>
                {ESTADOS_CIVIS.map((e) => <option key={e.value} value={e.value}>{e.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide block mb-1.5">
                Condição do imóvel *
              </label>
              <select
                value={condicaoImovel}
                onChange={(e) => setCondicaoImovel(e.target.value as '' | 'NOVO' | 'USADO')}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Selecione…</option>
                <option value="NOVO">Novo</option>
                <option value="USADO">Usado</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide block mb-1.5">
                Ocupação {ocupacoes.length > 0 ? '*' : <span className="font-normal normal-case text-amber-600">(dicionário vazio)</span>}
              </label>
              <select
                value={ocupacaoId}
                onChange={(e) => setOcupacaoId(e.target.value)}
                disabled={ocupacoes.length === 0}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-slate-50 disabled:text-slate-400"
              >
                <option value="">{ocupacoes.length === 0 ? 'Nenhuma ocupação cadastrada' : 'Selecione…'}</option>
                {ocupacoes.map((o) => <option key={o.id} value={o.id}>{o.descricao}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide block mb-1.5">
                Composição de renda *
              </label>
              <div className="relative">
                <input
                  type="number"
                  min={0}
                  max={100}
                  value={percentualRenda}
                  onChange={(e) => setPercentualRenda(e.target.value)}
                  className="w-full pr-8 pl-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-slate-400">%</span>
              </div>
            </div>
          </div>

          {/* Preview da pasta SharePoint */}
          {previewPasta && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg px-3 py-2.5">
              <p className="text-[10px] font-semibold text-blue-500 uppercase tracking-wide mb-0.5 flex items-center gap-1">
                <FolderOpen size={11} /> Pasta SharePoint que será criada
              </p>
              <p className="text-sm text-blue-800 font-mono break-all">{previewPasta}</p>
            </div>
          )}

          {/* Erro bloqueante */}
          {erro && (
            <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2 rounded-lg">
              {erro}
            </div>
          )}

          {/* Ações */}
          <div className="flex gap-3 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2 border border-slate-300 text-slate-600 rounded-lg text-sm font-medium hover:bg-slate-50 transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={salvando}
              className="flex-1 py-2 bg-otm-orange text-white rounded-lg text-sm font-medium hover:bg-otm-orange-dark disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
            >
              {salvando && (
                <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              )}
              {salvando ? 'Criando…' : 'Criar Operação'}
            </button>
          </div>
        </form>
        )}
      </div>
    </div>
  );
}
